// The in-game music library — instrumental deep house. Every track ships with
// an original generative groove (a 16-step deep-house engine: four-on-the-floor
// kick, offbeat hats, deep sub bass, lush 7th-chord pads and sparse keys —
// synthesised in the browser, zero copyright, works offline) so the soundtrack
// always plays. Drop a royalty-free file under public/music/<id>.mp3 and the
// player uses the real recording instead — see public/music/README.md for a
// curated list of Pixabay deep-house tracks (varied BPM / mood).

export type Scale = number[]

// Scales as semitone offsets within an octave
export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  minorPentatonic: [0, 3, 5, 7, 10],
  majorPentatonic: [0, 2, 4, 7, 9],
} as const

export interface GenerativeParams {
  /** MIDI root note (60 = C4) */
  root: number
  scale: Scale
  bpm: number
  /** 0..0.6 — shuffle amount applied to offbeat 16th steps (deep house stays low) */
  swing: number
  /** Chord roots as scale degrees (0-indexed), one per bar, looped */
  progression: number[]
  /** 16-step drum patterns (0 or 1) */
  kick: number[]
  snare: number[]
  hat: number[]
  /** 16-step melodic patterns: -99 = rest, else scale-degree offset from the bar chord root */
  bass: number[]
  lead: number[]
  /** Layer the lush sustained chord pad (the heart of deep house) */
  pad: boolean
  timbres: { pad: OscillatorType; lead: OscillatorType; bass: OscillatorType }
}

/** Category id for filtering the library. 'all' is a virtual scope (every track). */
export type MusicCategoryId = string

export interface MusicCategory {
  id: MusicCategoryId
  label: string
  description: string
}

// The genre/mood buckets the player can filter by. The virtual 'all' scope is
// handled in the helpers below and is not listed here. New categories are added
// as fresh tracks (and their Pixabay files) join the library.
export const MUSIC_CATEGORIES: MusicCategory[] = [
  {
    id: 'deep-house',
    label: 'Deep House',
    description: 'Ambiente de club nocturno: groove cálido, bajo profundo y pads.',
  },
  {
    id: 'indie-rock',
    label: 'Indie & Rock',
    description: 'Guitarras con energía y aire soñador, para subir el ritmo.',
  },
  {
    id: 'pop-dance',
    label: 'Pop & Dance',
    description: 'Pop luminoso y dance veraniego, animado y positivo.',
  },
  {
    id: 'acoustic',
    label: 'Acústica',
    description: 'Guitarra acústica y folk, cálido y relajado.',
  },
  {
    id: 'corporate',
    label: 'Motivación',
    description: 'Instrumentales motivadores, claros y optimistas.',
  },
]

export interface MusicTrack {
  id: string
  title: string
  artist: string
  mood: string
  /** Genre/mood bucket used by the library filter */
  category: MusicCategoryId
  /** Optional real audio file under /music; falls back to `gen` if absent/blocked */
  src?: string
  /** Generative groove used when the file is absent/blocked. Optional for tracks
   *  that ship a real recording — they reuse FALLBACK_GEN if their file fails. */
  gen?: GenerativeParams
}

// Shared deep-house drum building blocks
const KICK_4 = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0] // four on the floor
const CLAP_24 = [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] // backbeat on 2 & 4
const HAT_OFF = [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0] // offbeat 8th hats

// Deep house: original soundtrack with hand-authored generative grooves that
// double as the offline fallback.
const DEEP_HOUSE: MusicTrack[] = [
  {
    id: 'deep-melodic',
    title: 'Saque Melódico',
    artist: 'Pixabay · Deep House',
    mood: 'Deep house melódico',
    category: 'deep-house',
    src: '/music/deep-melodic.mp3',
    gen: {
      root: 62, // D
      scale: [...SCALES.dorian],
      bpm: 122,
      swing: 0.14,
      progression: [0, 5, 3, 4],
      kick: KICK_4,
      snare: CLAP_24,
      hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1],
      bass: [-99, -99, 0, -99, -99, -99, 0, -99, -99, -99, 0, -99, -99, -99, 0, -99],
      lead: [-99, -99, 4, -99, 2, -99, -99, 4, -99, -99, 7, -99, 4, -99, 2, -99],
      pad: true,
      timbres: { pad: 'triangle', lead: 'triangle', bass: 'sine' },
    },
  },
  {
    id: 'deep-soft',
    title: 'Sombra y Cristal',
    artist: 'Pixabay · Deep House',
    mood: 'Deep house suave, ambiental',
    category: 'deep-house',
    src: '/music/deep-soft.mp3',
    gen: {
      root: 60, // C
      scale: [...SCALES.dorian],
      bpm: 120,
      swing: 0.12,
      progression: [0, 3, 0, 3],
      kick: KICK_4,
      snare: CLAP_24,
      hat: HAT_OFF,
      bass: [-99, -99, 0, -99, -99, -99, 0, -99, -99, -99, 0, -99, -99, -99, 0, -99],
      lead: [-99, -99, -99, -99, -99, -99, 4, -99, -99, -99, -99, -99, -99, 2, -99, -99],
      pad: true,
      timbres: { pad: 'sawtooth', lead: 'sine', bass: 'sine' },
    },
  },
  {
    id: 'deep-night',
    title: 'Pista a Medianoche',
    artist: 'Pixabay · Deep House',
    mood: 'Deep house nocturno, atmosférico',
    category: 'deep-house',
    src: '/music/deep-night.mp3',
    gen: {
      root: 57, // A
      scale: [...SCALES.dorian],
      bpm: 119,
      swing: 0.1,
      progression: [0, 3, 4, 3],
      kick: KICK_4,
      snare: CLAP_24,
      hat: HAT_OFF,
      bass: [0, -99, -99, -99, -99, -99, -99, -99, 2, -99, -99, -99, -99, -99, -99, -99],
      lead: [-99, -99, -99, -99, -99, -99, -99, 4, -99, -99, -99, -99, -99, -99, 2, -99],
      pad: true,
      timbres: { pad: 'sawtooth', lead: 'sine', bass: 'sine' },
    },
  },
  {
    id: 'deep-groove',
    title: 'Rally Subterráneo',
    artist: 'Pixabay · Deep House',
    mood: 'Deep house con groove, enérgico',
    category: 'deep-house',
    src: '/music/deep-groove.mp3',
    gen: {
      root: 55, // G
      scale: [...SCALES.dorian],
      bpm: 124,
      swing: 0.16,
      progression: [0, 4, 5, 3],
      kick: KICK_4,
      snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
      hat: [0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 0, 0, 1, 1, 1],
      bass: [0, -99, -99, 0, -99, -99, 0, -99, -99, 0, -99, -99, 0, -99, 0, -99],
      lead: [-99, -99, 4, -99, -99, 2, -99, 4, -99, -99, -99, 5, -99, 4, -99, -99],
      pad: true,
      timbres: { pad: 'sawtooth', lead: 'triangle', bass: 'triangle' },
    },
  },
]

// Real Pixabay recordings (royalty-free). No hand-authored groove — if a file
// is ever missing they fall back to FALLBACK_GEN. Downloaded via
// scripts/fetch-music.mjs (see scripts/music-fetched.json for the source URLs).
const file = (
  id: string, title: string, artist: string, mood: string, category: MusicCategoryId,
): MusicTrack => ({ id, title, artist, mood, category, src: `/music/${id}.mp3` })

const INDIE_ROCK: MusicTrack[] = [
  file('rock-coffees-cold', 'Café Frío', 'Pixabay · Indie Rock', 'Indie rock animado', 'indie-rock'),
  file('rock-dreamy', 'Indie Soñador', 'Pixabay · Indie Rock', 'Indie rock soñador', 'indie-rock'),
  file('indie-rock-518575', 'Cuerdas Indie', 'Pixabay · Indie Rock', 'Indie rock con energía', 'indie-rock'),
  file('indie-rock-517927', 'Garaje', 'Pixabay · Indie Rock', 'Indie rock crudo', 'indie-rock'),
  file('indie-rock-513422', 'Tarde Indie', 'Pixabay · Indie Pop', 'Indie pop luminoso', 'indie-rock'),
]

const POP_DANCE: MusicTrack[] = [
  file('dance-summer-fun', 'Verano', 'Pixabay · Dance', 'Dance veraniego', 'pop-dance'),
  file('pop-hawaii', 'Hawái', 'TVARI · Pixabay', 'Pop tropical', 'pop-dance'),
  file('pop-dance-371361', 'Sol de Verano', 'Pixabay · Pop', 'Pop veraniego', 'pop-dance'),
  file('pop-dance-538160', 'Chiringuito', 'Pixabay · Pop', 'Pop divertido', 'pop-dance'),
  file('pop-dance-546980', 'Piscina', 'Pixabay · Pop', 'Pop luminoso', 'pop-dance'),
]

const ACOUSTIC: MusicTrack[] = [
  file('folk-guitar', 'Guitarra', 'Pixabay · Acústica', 'Folk acústico', 'acoustic'),
  file('acoustic-513152', 'Cuerdas al Sol', 'Pixabay · Acústica', 'Guitarra acústica', 'acoustic'),
  file('acoustic-122841', 'Ritmo Natural', 'Pixabay · Acústica', 'Acústica serena', 'acoustic'),
  file('acoustic-320427', 'Primavera', 'Pixabay · Acústica', 'Acústica primaveral', 'acoustic'),
  file('acoustic-142569', 'Bosque', 'Pixabay · Acústica', 'Acústica de naturaleza', 'acoustic'),
]

const CORPORATE: MusicTrack[] = [
  file('corp-motivation', 'Motivación', 'Pixabay · Motivación', 'Instrumental motivador', 'corporate'),
  file('corporate-496476', 'Impulso', 'Pixabay · Motivación', 'Motivador enérgico', 'corporate'),
  file('corporate-535031', 'Avance', 'Pixabay · Motivación', 'Corporativo optimista', 'corporate'),
  file('corporate-403408', 'Adelante', 'Pixabay · Motivación', 'Motivador positivo', 'corporate'),
  file('corporate-522437', 'Aventura', 'Pixabay · Motivación', 'Épico motivador', 'corporate'),
]

export const MUSIC_LIBRARY: MusicTrack[] = [
  ...DEEP_HOUSE,
  ...INDIE_ROCK,
  ...POP_DANCE,
  ...ACOUSTIC,
  ...CORPORATE,
]

export const DEFAULT_TRACK_ID = MUSIC_LIBRARY[0].id

/** Shared generative groove for tracks whose real file is missing/blocked */
export const FALLBACK_GEN: GenerativeParams = DEEP_HOUSE[0].gen!

/** Virtual category id meaning "every track" */
export const ALL_CATEGORY: MusicCategoryId = 'all'

/** Soundtrack a brand-new game (and a fresh install) starts on: the
 *  "Motivación" playlist, with its lead "Motivación" track. */
export const DEFAULT_CATEGORY_ID: MusicCategoryId = 'corporate'
export const DEFAULT_MUSIC_TRACK_ID = 'corp-motivation'

export function getTrack(id: string): MusicTrack {
  return MUSIC_LIBRARY.find(t => t.id === id) ?? MUSIC_LIBRARY[0]
}

/** Tracks within a category. The virtual 'all' scope returns the whole library. */
export function getTracksByCategory(catId: MusicCategoryId = ALL_CATEGORY): MusicTrack[] {
  if (catId === ALL_CATEGORY) return MUSIC_LIBRARY
  return MUSIC_LIBRARY.filter(t => t.category === catId)
}

// next/prev cycle *within* the active category (the chosen playlist). If the
// current track isn't in the scope (or the scope is empty) we fall back to the
// whole library so playback never gets stuck.
function scopeFor(id: string, catId: MusicCategoryId): MusicTrack[] {
  const scope = getTracksByCategory(catId)
  if (scope.length && scope.some(t => t.id === id)) return scope
  return MUSIC_LIBRARY
}

export function nextTrackId(id: string, catId: MusicCategoryId = ALL_CATEGORY): string {
  const scope = scopeFor(id, catId)
  const i = scope.findIndex(t => t.id === id)
  return scope[(i + 1) % scope.length].id
}

export function prevTrackId(id: string, catId: MusicCategoryId = ALL_CATEGORY): string {
  const scope = scopeFor(id, catId)
  const i = scope.findIndex(t => t.id === id)
  return scope[(i - 1 + scope.length) % scope.length].id
}
